<template>
    <b-card class="formations beneficial-owners" data-test="beneficial-owners">
        <b-card
            title="Beneficial Owners"
            class="mt-2 bordered-card beneficial-owners-section"
        >
            <beneficial-owner-index
                v-if="!showCreateOrEditBeneficialOwnerForm"
                :beneficial-owners-list="{
                    beneficialOwners,
                    beneficialOwnersDropdownList,
                }"
                :is-fetching-data="isFetchingData"
                :beneficial-owner-blue-print="beneficialOwnerBluePrint"
                @createBeneficialOwner="openCreateBeneficialOwnerForm"
                @edit="openCreateBeneficialOwnerForm"
                @remove="removeBeneficialOwner"
                @openCreateBeneficialOwnerForm="openCreateBeneficialOwnerForm"
                @createBeneficialOwnerFromDropdown="
                    createBeneficialOwnerFromDropdown
                "
            />

            <div class="mt-2 position-relative">
                <!-- create beneficial owner form -->
                <transition name="fade-bottom" mode="out-in">
                    <create-or-edit-beneficial-owner-form
                        v-if="showCreateOrEditBeneficialOwnerForm"
                        ref="createOrEdit"
                        :data-status="beneficialOwnerStatus"
                        :data-beneficial-owner="beneficialOwnerBluePrint"
                        :shareholder-options="shareholderOptions"
                        @cancel="showCreateOrEditBeneficialOwnerForm = false"
                        @createOrEdit="storeOrUpdateBeneficialOwner"
                    />
                </transition>
            </div>
        </b-card>

        <div class="d-flex justify-content-between mt-3">
            <b-button variant="outline-primary" @click="$emit('previous')">
                <!-- <img :src="'/icons/left-arrow.svg'" alt="left-arrow" /> -->
                <vector data-type="icons" data-width="16px" data-height="13px" data-name="left-arrow" />
                Previous
            </b-button>

            <b-button
                variant="primary"
                data-test="next"
                :disabled="loading || isFetchingData"
                class="
                    d-flex
                    justify-content-center
                    next-button
                    align-items-center
                "
                @click="next"
            >
                Next
                <normal-loader v-if="loading" class="ml-.5" />
                <img
                    v-else
                    :src="'/icons/right-arrow.svg'"
                    class="ml-.5"
                    alt="right-arrow"
                />
            </b-button>
        </div>
    </b-card>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import { BCard, BButton } from "bootstrap-vue";
import Formation from "@/models/Formation";
import { errorToast } from "@/views/helpers/toast";
import {
    BENEFICIAL_OWNER_FILL_OUT_ALL_FIELD_ERROR,
    SOMETHING_WENT_WRONG,
} from "@/constants/errors";
import { formatDate } from '@/helpers';
import CreateOrEditBeneficialOwnerForm from "./beneficial_owners/BeneficialOwnerCreateOrEdit.vue";
import BeneficialOwnerIndex from "./beneficial_owners/BeneficialOwnerIndex.vue";

export default {
    components: {
        BCard,
        BButton,
        BeneficialOwnerIndex,
        CreateOrEditBeneficialOwnerForm,
    },

    data() {
        return {
            beneficialOwnerBluePrint: {
                _id: undefined,
                name: undefined,
                idType: undefined,
                NRIC: undefined,
                dateOfExpiry: undefined,
                dob: undefined,
                numberOfShare: undefined,
                nationality: undefined,
                email: undefined,
                contactNumber: undefined,
                sourcesOfFunds: undefined,
                otherSource: undefined,
                address: undefined,
                shareholder: undefined,
            },
            beneficialOwners: [],
            beneficialOwnersDropdownList: [],
            beneficialOwnerKeys: [],
            shareholderOptions: [],
            beneficialOwnerTotalSteps: 0,
            beneficialOwnerStatus: undefined,
            showCreateOrEditBeneficialOwnerForm: false,

            loading: false,
            isFetchingData: true,
        };
    },

    async created() {
        this.isFetchingData = true;

        this.beneficialOwnerKeys = Object.keys(this.beneficialOwnerBluePrint);

        const { users, beneficial_owners, shareholders } = await Formation.getDataForBeneficialOwner();

        beneficial_owners.forEach(beneficialOwner => {
            this.beneficialOwners.push({
                _id: this.generateUniqueId(),
                name: beneficialOwner.user.user_info?.legal_name ?? beneficialOwner.user.name,
                idType: beneficialOwner.user.user_info?.id_type
                    ? {
                        id: beneficialOwner.user.user_info.id_type?.id,
                        name: beneficialOwner.user.user_info.id_type?.name,
                    }
                    : undefined,
                nationality: beneficialOwner.user.user_info?.nationality
                    ? {
                        value: beneficialOwner.user.user_info.nationality.id,
                        text: beneficialOwner.user.user_info.nationality.name,
                    }
                    : undefined,
                NRIC: beneficialOwner.user.user_info
                    ? beneficialOwner.user.user_info.NRIC
                    : undefined,
                dateOfExpiry: beneficialOwner.user.user_info
                    ? formatDate(beneficialOwner.user.user_info.date_of_expiry)
                    : null,
                dob: beneficialOwner.user.user_info
                    ? formatDate(beneficialOwner.user.user_info.DOB)
                    : undefined,
                numberOfShare: beneficialOwner.number_of_shares,
                email: beneficialOwner.user.email,
                contactNumber: beneficialOwner.user.user_info
                    ? beneficialOwner.user.user_info.contact_number
                    : undefined,
                sourcesOfFunds: beneficialOwner.source_of_funds 
                    ? this.localizeSourceOfFunds(beneficialOwner)
                    : [],
                otherSource: beneficialOwner?.other_reasons?.description,
                address: beneficialOwner.user.user_info
                    ? beneficialOwner.user.user_info.address
                    : undefined,
                shareholder : beneficialOwner?.shareholder ? {
                    userId: beneficialOwner?.shareholder.id,
                    name: beneficialOwner?.shareholder?.user_info?.legal_name ?? beneficialOwner?.shareholder.name,
                    email: beneficialOwner?.shareholder.email
                } : undefined,
            });
        });

        users.forEach(user => {
            if (
                // beneficialOwner.name === user.name &&
                !this.beneficialOwners.find(
                    beneficialOwner =>
                        beneficialOwner.email === user.email
                )
            ) {
                this.beneficialOwnersDropdownList.push({
                    _id: this.generateUniqueId(),
                    name: user?.user_info?.legal_name ?? user.name,
                    idType: user.user_info?.id_type
                        ? {
                            id: user.user_info.id_type?.id,
                            name: user.user_info.id_type?.name,
                        }
                        : undefined,
                    nationality: user.user_info?.nationality
                        ? {
                            value: user.user_info.nationality.id,
                            text: user.user_info.nationality.name,
                        }
                        : undefined,
                    dateOfExpiry: formatDate(user.user_info?.date_of_expiry),
                    NRIC: user.user_info
                        ? user.user_info.NRIC
                        : undefined,
                    dob: user.user_info
                        ? formatDate(user.user_info.DOB)
                        : undefined,
                    email: user.email,
                    contactNumber: user.user_info
                        ? user.user_info.contact_number
                        : undefined,
                    address: user.user_info
                        ? user.user_info.address
                        : undefined,
                    numberOfShare: user.shareholder ? user.shareholder.number_of_shares : undefined,
                });
            }
        });
        
        this.shareholderOptions = shareholders.map(shareholder => ({
            userId: shareholder.user.id,
            name: shareholder.user?.user_info?.legal_name ?? shareholder.user.name,
            email: shareholder.user.email,
        }));

        this.isFetchingData = false;
    },

    methods: {
        localizeSourceOfFunds(beneficialOwner) {
            return beneficialOwner.source_of_funds.map(sof => sof.id);
        },

        createBeneficialOwnerFromDropdown(beneficialOwner) {           
            this.openCreateBeneficialOwnerForm(beneficialOwner);
        },

        openCreateBeneficialOwnerForm(
            beneficialOwner = this.beneficialOwnerBluePrint,
            status = "create"
        ) {
            // reset Blue Print data
            Object.keys(this.beneficialOwnerBluePrint).forEach(key => {
                this.beneficialOwnerBluePrint[key] = beneficialOwner[key] ?? null;
            });

            this.showCreateOrEditBeneficialOwnerForm = true;
            this.beneficialOwnerStatus = status;
        },

        storeOrUpdateBeneficialOwner(beneficialOwner) {
            if (this.beneficialOwnerStatus === "edit") {
                this.beneficialOwners.forEach(existingBeneficialOwner => {
                    if (existingBeneficialOwner._id === beneficialOwner._id) {
                        Object.keys(this.beneficialOwnerBluePrint).forEach(
                            key => {
                                // eslint-disable-next-line no-param-reassign
                                existingBeneficialOwner[key] =
                                    beneficialOwner[key];
                            }
                        );
                    }
                });
            }

            if (this.beneficialOwnerStatus === "create") {
                // eslint-disable-next-line no-param-reassign
                beneficialOwner._id = this.generateUniqueId();
                this.beneficialOwners.push(beneficialOwner);
            }

            this.showCreateOrEditBeneficialOwnerForm = false;

            // dropdown beneficialOwner
            this.beneficialOwnersDropdownList =
                this.beneficialOwnersDropdownList.filter(
                    user =>
                        user.name !== beneficialOwner.name &&
                        user.email !== beneficialOwner.email
                );
        },

        removeBeneficialOwner(beneficialOwner) {
            this.beneficialOwners = this.beneficialOwners.filter(
                // eslint-disable-next-line no-param-reassign
                existingBeneficialOwner =>
                    existingBeneficialOwner._id !== beneficialOwner._id
            );
            this.beneficialOwnersDropdownList.push(beneficialOwner);
        },

        generateUniqueId() {
            return Math.round(Math.random() * 1000);
        },

        async next() {
            this.loading = true;
            const { beneficialOwners } = this;

            if (!this.validate(beneficialOwners)) {
                errorToast(BENEFICIAL_OWNER_FILL_OUT_ALL_FIELD_ERROR);
                this.loading = false;
                return;
            }

            await Formation.storeBeneficialOwners({
                beneficialOwners: this.beneficialOwners,
            })
                .then(() => {
                    this.loading = false;
                    this.$emit("next");
                })
                .catch(e => {
                    errorToast(SOMETHING_WENT_WRONG);
                    console.error(
                        "error while trying to store beneficial owners in formation",
                        e
                    );
                    this.loading = false;
                });
        },

        validate(beneficialOwners) {

            // beneficialOwners.forEach(beneficialOwner => {
            //     console.log(Object.keys(beneficialOwner).filter(key => 
            //         beneficialOwner[key] && !key.includes('dateOfExpiry') && !key.includes("otherSource")
            //     ).length - (beneficialOwner.sourcesOfFunds.length >= 1 ? 0 : 1), Object.keys(beneficialOwner).length - ( 1 + 1));
            // });

            // eslint-disable-next-line array-callback-return
            const validBeneficialOwnerCount = beneficialOwners.filter(
                beneficialOwner =>
                    Object.keys(beneficialOwner).filter(
                        key => beneficialOwner[key] && !key.includes('dateOfExpiry') && !key.includes("otherSource")
                        // need to minus 1 if sof array doesn't have value
                    ).length - (beneficialOwner.sourcesOfFunds.length >= 1 ? 0 : 1) === this.perfectBeneficialOwnerLength(beneficialOwner)
            ).length;

            // ignore otherSource
            return validBeneficialOwnerCount === beneficialOwners.length;
        },

        perfectBeneficialOwnerLength(beneficialOwner) {
            // ignore, date of expiry, other source
            return Object.keys(beneficialOwner).length - (1 + 1);
        }
    },
};
</script>

<style>
.formations.beneficial-owners .card-body {
    padding: 1.3rem;
}
</style>
